/*
 * Refresh product page (sections/rd-main-product.liquid):
 * gallery (thumbs/dots <-> scroll-snap track), option pills -> variant, quantity stepper,
 * live "Add to cart · $total", stock line, free-shipping progress, AJAX add (cart drawer).
 */
(() => {
  const root = document.querySelector('[data-rd-product]');
  if (!root) return;
  const data = JSON.parse(root.querySelector('[data-rd-product-json]').textContent);
  const form = root.querySelector('[data-rd-product-form]');
  const $ = (sel) => root.querySelector(sel);

  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: data.currency || 'USD' }).format(
      cents / 100
    );

  let variant = data.variants.find((v) => v.id === data.selected) || data.variants[0];
  const qtyInput = $('[data-rd-qty-input]') || document.createElement('input');
  const qty = () => Math.max(1, parseInt(qtyInput.value, 10) || 1);
  // Most of this variant that can still go in the cart: stock minus what's already there
  // (only when Shopify tracks stock and won't oversell), capped by any quantity-rule max.
  const tracked = (v) => v.inventory_management === 'shopify' && v.inventory_policy !== 'continue';
  const maxQty = () => {
    let max = 99;
    if (tracked(variant)) max = Math.max(0, variant.inventory_quantity - (variant.in_cart || 0));
    if (variant.quantity_max) max = Math.min(max, Math.max(0, variant.quantity_max - (variant.in_cart || 0)));
    return max;
  };

  /* ---------- Gallery ---------- */
  const track = $('[data-rd-track]');
  const slides = track ? [...track.children] : [];
  const markCurrent = (index) => {
    root.querySelectorAll('[data-rd-thumb]').forEach((b) => {
      if (Number(b.dataset.rdThumb) === index) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
  };
  const goTo = (index) => {
    const slide = slides[index];
    if (!slide) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    markCurrent(index);
  };
  root.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-rd-thumb]');
    if (thumb) goTo(Number(thumb.dataset.rdThumb));
  });
  if (track && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.intersectionRatio > 0.6 && markCurrent(slides.indexOf(e.target))),
      { root: track, threshold: [0.6] }
    );
    slides.forEach((s) => io.observe(s));
  }

  /* ---------- Render state ---------- */
  const render = () => {
    if (!$('[data-rd-add]')) return; // pre-order plants: no cart controls on this page
    const max = maxQty();
    const clamped = Math.min(qty(), Math.max(1, max));
    if (qtyInput.value !== String(clamped)) qtyInput.value = clamped;
    qtyInput.max = Math.max(1, max);
    const plus = $('[data-rd-qty="1"]');
    if (plus) plus.disabled = !variant.available || qty() >= max;
    const note = $('[data-rd-qty-note]');
    if (note) {
      const capped = variant.available && max < 99 && qty() >= max;
      note.hidden = !capped;
      if (capped) {
        const inCart = variant.in_cart || 0;
        note.textContent =
          max <= 0
            ? `You already have all ${inCart} available in your cart.`
            : inCart
              ? `Only ${max} more available (${inCart} already in your cart).`
              : `Only ${max} available.`;
      }
    }
    const total = variant.price * qty();
    $('[data-rd-variant-id]').value = variant.id;
    $('[data-rd-price]').textContent = money(variant.price);

    const onSale = variant.compare_at_price > variant.price;
    $('[data-rd-was]').hidden = !onSale;
    $('[data-rd-sale]').hidden = !onSale;
    if (onSale) $('[data-rd-was-amount]').textContent = money(variant.compare_at_price);

    const add = $('[data-rd-add]');
    add.disabled = !variant.available || max <= 0;
    $('[data-rd-add-label]').textContent = !variant.available ? 'Sold out' : max <= 0 ? 'All in your cart' : `Add to cart · ${money(total)}`;

    const stock = $('[data-rd-stock]');
    const tracked = variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue';
    if (!variant.available) {
      stock.className = 'rd-buy__stock rd-buy__stock--out';
      stock.textContent = data.preorder ? 'Sold out · Open for pre-order' : 'Sold out';
    } else if (tracked && variant.inventory_quantity > 0 && variant.inventory_quantity <= data.lowStock) {
      stock.className = 'rd-buy__stock';
      stock.textContent = `In stock · Only ${variant.inventory_quantity} left`;
    } else {
      stock.className = 'rd-buy__stock';
      stock.textContent = data.plant ? 'In stock · ships now' : 'In stock';
    }

    const sku = $('[data-rd-sku]');
    if (sku) {
      sku.textContent = variant.sku || '';
      $('[data-rd-sku-row]').hidden = !variant.sku;
    }

    const ship = $('[data-rd-ship]');
    if (ship) {
      const threshold = Number(ship.dataset.threshold);
      const inCart = Number(ship.dataset.cartTotal) || 0;
      const sum = inCart + total;
      const left = threshold - sum;
      $('[data-rd-ship-msg]').textContent =
        left > 0 ? `You are ${money(left)} away from free shipping.` : 'Free shipping unlocked on this order.';
      $('[data-rd-ship-bar]').style.width = `${Math.min(100, Math.round((sum / threshold) * 100))}%`;
    }
  };

  /* ---------- Options ---------- */
  root.addEventListener('change', (event) => {
    if (!event.target.matches('[data-rd-option]')) return;
    const chosen = [...root.querySelectorAll('[data-rd-option]:checked')].map((i) => i.value);
    const match = data.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
    root.querySelectorAll('[data-rd-option-current]').forEach((el) => (el.textContent = chosen[Number(el.dataset.rdOptionCurrent)]));
    if (!match) {
      $('[data-rd-add]').disabled = true;
      $('[data-rd-add-label]').textContent = 'Unavailable';
      return;
    }
    variant = match;
    render();
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);
    if (variant.media_id) {
      const index = slides.findIndex((s) => s.dataset.mediaId === String(variant.media_id));
      if (index >= 0) goTo(index);
    }
  });

  /* ---------- Quantity ---------- */
  root.addEventListener('click', (event) => {
    const step = event.target.closest('[data-rd-qty]');
    if (!step) return;
    qtyInput.value = Math.min(Math.max(1, maxQty()), Math.max(1, qty() + Number(step.dataset.rdQty)));
    render();
  });
  qtyInput.addEventListener('input', render);

  /* ---------- Cart sync: in-cart counts, free-shipping total ---------- */
  const syncCart = async () => {
    const fresh = await (await fetch(`${window.routes?.cart_url || '/cart'}.js`)).json();
    data.variants.forEach((v) => {
      v.in_cart = fresh.items.filter((i) => i.variant_id === v.id).reduce((n, i) => n + i.quantity, 0);
    });
    const ship = $('[data-rd-ship]');
    if (ship) ship.dataset.cartTotal = fresh.total_price;
    return fresh;
  };

  /* ---------- Add to cart (cart drawer, like Dawn's product-form) ---------- */
  form?.addEventListener('submit', async (event) => {
    if (event.submitter && event.submitter.closest('.shopify-payment-button')) return;
    event.preventDefault();
    const add = $('[data-rd-add]');
    const error = $('[data-rd-error]');
    if (add.disabled) return;
    add.disabled = true;
    add.classList.add('is-loading');
    error.hidden = true;

    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = new FormData(form);
    if (cart) {
      body.append('sections', cart.getSectionsToRender().map((s) => s.id));
      body.append('sections_url', window.location.pathname);
      cart.setActiveElement(add);
    }
    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const result = await response.json();
      if (result.status) {
        error.textContent = result.description || result.message || 'Could not add to cart.';
        error.hidden = false;
        // Shopify can reject the request yet still add what's in stock ("Only 1 item was added
        // to your cart due to availability"): if the cart changed, show it in the drawer.
        const before = variant.in_cart || 0;
        await syncCart();
        if (cart && (variant.in_cart || 0) > before) {
          const ids = cart.getSectionsToRender().map((s) => s.id).join(',');
          const sections = await (await fetch(`${window.location.pathname}?sections=${ids}`)).json();
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rd-product', productVariantId: variant.id });
          }
          cart.classList.remove('is-empty');
          cart.renderContents({ id: variant.id, sections });
        }
        qtyInput.value = 1;
        return;
      }
      if (!cart) {
        window.location = window.routes?.cart_url || '/cart';
        return;
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rd-product', productVariantId: variant.id, cartData: result });
      }
      cart.classList.remove('is-empty');
      cart.renderContents(result);
      await syncCart();
      qtyInput.value = 1;
    } catch (e) {
      console.error('[rd-product]', e);
      form.submit();
    } finally {
      add.classList.remove('is-loading');
      render(); // re-enables the button, or keeps it off when everything in stock is in the cart
    }
  });

  render();
})();
